//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { YouTubeVisibility } from '@models/YouTubeVisibility';
import { IYouTubeFormState } from '@universal-scheduling/models/SchedulerPostDetailsModels';

export const DEFAULT_YOUTUBE_POST_DETAILS_FORM_STATE: IYouTubeFormState = {
  postType: UniversalPostType.YouTubeAutomaticPost,
  postNow: false,
  license: 'youtube',
};

export const YOUTUBE_MAX_DESCRIPTION_LENGTH = 5000;
export const YOUTUBE_MAX_TITLE_LENGTH = 100;
export const YOUTUBE_MAX_PLAYLIST_TITLE_LENGTH = 150;
export const YOUTUBE_MAX_TAGS_LENGTH = 500;

export const YouTubePostDetailsFormFieldErrorText = {
  TextTooLong: `Please enter ${YOUTUBE_MAX_DESCRIPTION_LENGTH} characters or less.`,
  TitleTooLong: `Please enter ${YOUTUBE_MAX_TITLE_LENGTH} characters or less.`,
  TagsTooLong: `Tags can only be ${YOUTUBE_MAX_TAGS_LENGTH} characters long total.`,
  CategoryRequired: 'Please select a category.',
  LicenseRequired: 'Please select a license.',
  PrivacyRequired: 'Please select a privacy setting.',
  TitleRequired: 'Please enter a title.',
  IsSafeForKidsRequired: 'Please select whether this content is safe for kids.',
  PlaylistTitleTooLong: `Please enter ${YOUTUBE_MAX_PLAYLIST_TITLE_LENGTH} characters or less.`,
} as const;

export const YouTubePostDetailsFormTooltipText = {
  Tags: 'Add descriptive keywords to help highlight your content in searches.',
  Public: 'Public videos can be viewed by and shared with all YouTube users.',
  Private: 'Private videos can only be viewed by you and your chosen users.',
  Unlisted: 'Unlisted videos can be viewed and shared by anyone with the link.',
  MadeForKids: `Videos in this category may include those where:
  1. Children are the primary audience of the video.
  2. Children are not the primary audience, but the video is still directed at children because it features actors, characters, activities, games, songs, stories, or other subject matter that is intended for children.`,
  NotForKids:
    'Videos in this category are considered to have the potential to appeal to a general audience but not be intended specifically for children, or be intended for a teen or older audience. General audience content should be set as “not made for kids."',
  AllowEmbeds: 'Allow other users to embed your video on a different website.',
} as const;

export const YOUTUBE_PRIVACY_ITEMS = [
  {
    label: 'Public',
    value: YouTubeVisibility.Public,
    tooltip: YouTubePostDetailsFormTooltipText.Public,
  },
  {
    label: 'Unlisted',
    value: YouTubeVisibility.Unlisted,
    tooltip: YouTubePostDetailsFormTooltipText.Unlisted,
  },
  {
    label: 'Private',
    value: YouTubeVisibility.Private,
    tooltip: YouTubePostDetailsFormTooltipText.Private,
  },
];

export const YouTubeVideoIsSafeForKids = {
  No: 'no',
  Yes: 'yes',
} as const;

export type YouTubeVideoIsSafeForKids =
  (typeof YouTubeVideoIsSafeForKids)[keyof typeof YouTubeVideoIsSafeForKids];

export const MAX_YOUTUBE_PLAYLIST_ITEM_CHARACTER_LENGTH = 15;
